import { useState } from 'react';
import { Button, Input } from 'antd';
import { KeyValueRows, type KeyValueRow } from '@presentation/components/workbench/KeyValueRows';

interface BulkEditableKeyValueRowsProps<T extends KeyValueRow> {
  rows: T[];
  onChange: (rows: T[]) => void;
  showDescription?: boolean;
  typeOptions?: { label: string; value: string }[];
  createRow: () => T;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}

const rowsToText = (rows: KeyValueRow[]): string =>
  rows
    .map((row) => {
      const line = `${row.key}: ${row.value ?? ''}`;
      return row.isEnabled ? line : `// ${line}`;
    })
    .join('\n');

const textToRows = <T extends KeyValueRow>(text: string, createRow: () => T): T[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => {
      const isEnabled = !line.startsWith('//');
      const content = isEnabled ? line : line.replace(/^\/\/\s?/, '');
      const separatorIndex = content.indexOf(':');
      const key = separatorIndex === -1 ? content.trim() : content.slice(0, separatorIndex).trim();
      const value = separatorIndex === -1 ? '' : content.slice(separatorIndex + 1).trim();
      return { ...createRow(), key, value, isEnabled };
    });

/**
 * A key/value editor with a Postman-style "Bulk Edit" toggle: switches the
 * row table for a plain-text `key: value` per line textarea (a `//` prefix
 * disables that line), applied back to rows when switching back to the
 * table view. Bulk mode only round-trips key/value/enabled — descriptions
 * and (for params) the query/path type aren't representable in the raw
 * text format, so they're dropped for any row touched in bulk mode. This
 * mirrors Postman's own bulk editor rather than inventing a richer format.
 */
export const BulkEditableKeyValueRows = <T extends KeyValueRow>({
  rows,
  onChange,
  showDescription = false,
  typeOptions,
  createRow,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
}: BulkEditableKeyValueRowsProps<T>) => {
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState('');

  const enterBulkMode = () => {
    setBulkText(rowsToText(rows));
    setBulkMode(true);
  };

  const applyAndExitBulkMode = () => {
    onChange(textToRows(bulkText, createRow));
    setBulkMode(false);
  };

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex justify-end">
        <Button
          type="link"
          size="small"
          className="!px-0 !h-auto"
          onClick={bulkMode ? applyAndExitBulkMode : enterBulkMode}
        >
          {bulkMode ? 'Key-Value Edit' : 'Bulk Edit'}
        </Button>
      </div>

      {bulkMode ? (
        <div className="flex flex-1 flex-col gap-1.5">
          <Input.TextArea
            value={bulkText}
            onChange={(event) => setBulkText(event.target.value)}
            autoSize={{ minRows: 6, maxRows: 20 }}
            className="font-mono text-xs"
            placeholder={'key: value\n// disabled-key: value'}
          />
          <span className="text-xs text-gray-400">
            One per line as <code>key: value</code>. Prefix a line with <code>//</code> to disable
            it. Descriptions
            {typeOptions ? ' and types' : ''} aren&apos;t preserved for rows edited here.
          </span>
        </div>
      ) : (
        <KeyValueRows
          rows={rows}
          onChange={onChange}
          showDescription={showDescription}
          typeOptions={typeOptions}
          createRow={createRow}
          keyPlaceholder={keyPlaceholder}
          valuePlaceholder={valuePlaceholder}
        />
      )}
    </div>
  );
};
